'use client';
import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { getRankDotColor, Rank } from '@/data/mockData';
import RankBadge from '@/components/ui/RankBadge';

interface HistoryRow {
  id: string;
  name: string;
  prevRank: Rank;
  newRank: Rank;
  direction: 'up' | 'down' | 'same';
  date: string;
  gsv: number;
  psv: number;
  reason: string;
}

const HISTORY: HistoryRow[] = [
  { id: 'hist-001', name: 'Priya Nambiar', prevRank: 'Diamond', newRank: 'Blue Diamond', direction: 'up', date: 'Mar 2026', gsv: 31200, psv: 310, reason: 'Achieved 3 G1 Breakaway Execs' },
  { id: 'hist-002', name: 'Derek Huang', prevRank: 'Executive', newRank: 'Emerald', direction: 'up', date: 'Feb 2026', gsv: 12400, psv: 350, reason: 'Sustained 10,000+ GSV for 3 months' },
  { id: 'hist-003', name: 'Aisha Okonkwo', prevRank: 'Executive', newRank: 'Provisional Executive', direction: 'down', date: 'Feb 2026', gsv: 1850, psv: 115, reason: 'GSV fell below 2,000 maintenance' },
  { id: 'hist-004', name: 'Kwame Asante', prevRank: 'Brand Partner Lapis', newRank: 'Brand Partner Ruby', direction: 'up', date: 'Jan 2026', gsv: 2100, psv: 180, reason: 'Completed Q2 qualification stage' },
  { id: 'hist-005', name: 'Lena Kowalski', prevRank: 'Brand Partner Gold', newRank: 'Brand Partner Lapis', direction: 'up', date: 'Jan 2026', gsv: 1640, psv: 160, reason: 'Entered Q1 qualification' },
  { id: 'hist-006', name: 'Natalia Ferreira', prevRank: 'Brand Partner Ruby', newRank: 'Executive', direction: 'up', date: 'Dec 2025', gsv: 5800, psv: 220, reason: 'Completed 3-month qualification window' },
  { id: 'hist-007', name: 'Simone Okafor', prevRank: 'Distributor', newRank: 'Brand Partner Gold', direction: 'up', date: 'Nov 2025', gsv: 680, psv: 140, reason: 'Entered LOI qualification' },
  { id: 'hist-008', name: 'Jerome Castillo', prevRank: 'Emerald', newRank: 'Diamond', direction: 'up', date: 'Oct 2025', gsv: 18700, psv: 280, reason: '15,000+ GSV with 2 G1 Breakaway Execs' },
  { id: 'hist-009', name: 'Fatima Al-Rashid', prevRank: 'Brand Partner Ruby', newRank: 'Executive', direction: 'up', date: 'Sep 2025', gsv: 4200, psv: 240, reason: 'Q2 qualification complete, ADR maintained' },
  { id: 'hist-010', name: 'Carlos Mendes', prevRank: 'Brand Partner Gold', newRank: 'Brand Partner Lapis', direction: 'up', date: 'Sep 2025', gsv: 890, psv: 130, reason: 'LOI to Q1 progression' },
  { id: 'hist-011', name: 'Tomás Varela', prevRank: 'Brand Partner Gold', newRank: 'Distributor', direction: 'down', date: 'Aug 2025', gsv: 340, psv: 75, reason: 'PSV dropped below 100 — inactive' },
  { id: 'hist-012', name: 'Obinna Ezeh', prevRank: 'Distributor', newRank: 'Brand Partner Gold', direction: 'up', date: 'Jul 2025', gsv: 490, psv: 155, reason: 'Entered LOI qualification stage' },
];

export default function RankHistoryTable() {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-300">Rank Advancement History</h3>
        <span className="text-xs text-zinc-600 font-mono">{HISTORY.length} events</span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-zinc-800">
            {['Distributor', 'Previous Rank', 'New Rank', 'Direction', 'Period', 'GSV', 'PSV', 'Reason'].map(h => (
              <th key={`rhth-${h}`} className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-2.5">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HISTORY.map(row => (
            <tr key={row.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20 transition-colors">
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getRankDotColor(row.newRank)}`} />
                  <span className="text-sm font-medium text-zinc-200 whitespace-nowrap">{row.name}</span>
                </div>
              </td>
              <td className="px-4 py-2.5"><RankBadge rank={row.prevRank} size="xs" /></td>
              <td className="px-4 py-2.5"><RankBadge rank={row.newRank} size="xs" /></td>
              <td className="px-4 py-2.5">
                <div className={`flex items-center gap-1 text-xs font-semibold ${
                  row.direction === 'up' ? 'text-emerald-400' : row.direction === 'down' ? 'text-red-400' : 'text-zinc-500'
                }`}>
                  {row.direction === 'up' ? <TrendingUp size={13} /> : row.direction === 'down' ? <TrendingDown size={13} /> : <Minus size={13} />}
                  {row.direction === 'up' ? 'Promoted' : row.direction === 'down' ? 'Demoted' : 'No Change'}
                </div>
              </td>
              <td className="px-4 py-2.5"><span className="text-xs font-mono text-zinc-500">{row.date}</span></td>
              <td className="px-4 py-2.5"><span className="text-xs font-mono text-zinc-300">{(row.gsv / 1000).toFixed(1)}K</span></td>
              <td className="px-4 py-2.5">
                <span className={`text-xs font-mono font-semibold ${row.psv >= 100 ? 'text-zinc-300' : 'text-red-400'}`}>{row.psv}</span>
              </td>
              <td className="px-4 py-2.5">
                <span className="text-xs text-zinc-500 max-w-[200px] truncate block">{row.reason}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}